<template>
  <div class="entity-list-page my-library-page">

    <header class="list-header">
      <div>
        <h1>Library</h1>
        <p class="list-sub">Saved AI searches and PubMed articles from your clinical consultations</p>
      </div>
      <div class="header-actions">
        <router-link :to="{ path: '/learning', query: { tab: 'agent' } }" class="btn btn-primary">
          <i class="fas fa-stethoscope"></i>
          Vet Consult
        </router-link>
        <router-link to="/learning/my-courses" class="btn btn-secondary">
          <i class="fas fa-graduation-cap"></i>
          My courses
        </router-link>
      </div>
    </header>

    <!-- Loading -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading your library…</p>
    </div>

    <div v-else class="library-content">

      <!-- Stats Row -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-icon articles">
            <i class="fas fa-flask"></i>
          </div>
          <div class="stat-info">
            <h3>{{ stats.total_saved || 0 }}</h3>
            <p>Saved Articles</p>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon favorites">
            <i class="fas fa-star"></i>
          </div>
          <div class="stat-info">
            <h3>{{ stats.favorites || 0 }}</h3>
            <p>Favorites</p>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon searches">
            <i class="fas fa-search"></i>
          </div>
          <div class="stat-info">
            <h3>{{ savedSearches.length }}</h3>
            <p>Saved Searches</p>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon folders">
            <i class="fas fa-folder"></i>
          </div>
          <div class="stat-info">
            <h3>{{ stats.folders || 0 }}</h3>
            <p>Folders</p>
          </div>
        </div>
      </div>

      <!-- Tab Bar -->
      <div class="library-tabs">
        <button
          :class="['tab-btn', { active: activeTab === 'articles' }]"
          @click="activeTab = 'articles'"
        >
          <i class="fas fa-flask"></i>
          Saved Articles
          <span class="tab-count">{{ articles.length }}</span>
        </button>
        <button
          :class="['tab-btn', { active: activeTab === 'searches' }]"
          @click="activeTab = 'searches'"
        >
          <i class="fas fa-search"></i>
          Saved Searches
          <span class="tab-count">{{ savedSearches.length }}</span>
        </button>
      </div>

      <!-- ── Articles Tab ─────────────────────────────── -->
      <div v-if="activeTab === 'articles'" class="tab-content">

        <!-- Filter chips -->
        <div v-if="articles.length > 0" class="filter-bar">
          <button
            :class="['filter-chip', { active: articleFilter === 'all' }]"
            @click="articleFilter = 'all'"
          >All</button>
          <button
            :class="['filter-chip star-chip', { active: articleFilter === 'favorites' }]"
            @click="articleFilter = 'favorites'"
          >
            <i class="fas fa-star"></i> Favorites
          </button>
          <button
            v-for="f in folderNames"
            :key="f"
            :class="['filter-chip', { active: articleFilter === f }]"
            @click="articleFilter = f"
          >
            <i class="fas fa-folder"></i> {{ f }}
          </button>
        </div>

        <!-- Empty state -->
        <div v-if="filteredArticles.length === 0" class="empty-state">
          <div class="empty-icon"><i class="fas fa-flask"></i></div>
          <h3>No saved articles yet</h3>
          <p>
            Run an AI clinical consultation, then click the
            <i class="far fa-bookmark"></i> bookmark icon on any PubMed
            source to save it here.
          </p>
          <router-link to="/learning" class="btn btn-primary">
            <i class="fas fa-robot"></i> Start a Consultation
          </router-link>
        </div>

        <!-- Article list -->
        <div v-else class="articles-list">
          <div
            v-for="article in filteredArticles"
            :key="article.id"
            class="article-card"
          >
            <div class="article-card-header">
              <div class="article-meta">
                <span v-if="article.journal" class="article-journal">{{ article.journal }}</span>
                <span v-if="article.publication_date" class="article-year">{{ article.publication_date }}</span>
              </div>
              <div class="article-actions">
                <button
                  class="action-btn"
                  :class="{ 'fav-active': article.is_favorite }"
                  :title="article.is_favorite ? 'Remove from favorites' : 'Add to favorites'"
                  @click="toggleFavorite(article)"
                >
                  <i :class="article.is_favorite ? 'fas fa-star' : 'far fa-star'"></i>
                </button>
                <a
                  :href="article.url || `https://pubmed.ncbi.nlm.nih.gov/${article.external_id}/`"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="action-btn"
                  title="Open in PubMed"
                >
                  <i class="fas fa-external-link-alt"></i>
                </a>
                <button
                  class="action-btn delete-btn"
                  title="Remove from library"
                  @click="deleteArticle(article)"
                >
                  <i class="fas fa-trash-alt"></i>
                </button>
              </div>
            </div>

            <h4 class="article-title">{{ article.title }}</h4>

            <p v-if="authorLine(article)" class="article-authors">
              {{ authorLine(article) }}
            </p>

            <div class="article-footer">
              <span class="article-pmid">PMID: {{ article.external_id }}</span>
              <span v-if="article.original_search_query" class="article-via">
                <i class="fas fa-robot"></i>
                Found via: "{{ truncate(article.original_search_query, 60) }}"
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Saved Searches Tab ──────────────────────── -->
      <div v-if="activeTab === 'searches'" class="tab-content">

        <!-- Empty state -->
        <div v-if="savedSearches.length === 0" class="empty-state">
          <div class="empty-icon"><i class="fas fa-search"></i></div>
          <h3>No saved searches yet</h3>
          <p>
            After running an AI consultation, click
            <i class="far fa-bookmark"></i> "Save Search" to store the
            query for quick re-use.
          </p>
          <router-link to="/learning" class="btn btn-primary">
            <i class="fas fa-robot"></i> Start a Consultation
          </router-link>
        </div>

        <div v-else class="searches-list">
          <div
            v-for="search in savedSearches"
            :key="search.id"
            class="search-card"
          >
            <!-- Card header row -->
            <div class="search-card-header">
              <span class="search-type-badge" :class="`type-${search.search_type.toLowerCase()}`">
                {{ searchTypeLabel(search.search_type) }}
              </span>
              <div class="search-meta-row">
                <span v-if="search.use_count > 0" class="use-count">
                  <i class="fas fa-redo"></i> Run {{ search.use_count }}×
                </span>
                <span class="search-date">Saved {{ formatDate(search.created_at) }}</span>
              </div>
            </div>

            <h4 class="search-name">{{ search.name }}</h4>
            <p class="search-query">"{{ search.query }}"</p>

            <!-- Notes display / edit -->
            <div v-if="editingNotesId !== search.id" class="search-notes-row">
              <p v-if="search.notes" class="search-notes">
                <i class="fas fa-sticky-note"></i>
                {{ search.notes }}
              </p>
              <button
                class="edit-notes-btn"
                :title="search.notes ? 'Edit note' : 'Add a note'"
                @click="startEditNotes(search)"
              >
                <i :class="search.notes ? 'fas fa-pencil-alt' : 'fas fa-plus'"></i>
                {{ search.notes ? '' : 'Add note' }}
              </button>
            </div>
            <div v-else class="search-notes-edit">
              <textarea
                v-model="editNotesValue"
                class="notes-textarea"
                placeholder="Add a note for your reference…"
                rows="2"
                @keydown.esc="cancelEditNotes"
              />
              <div class="notes-edit-actions">
                <button class="btn btn-primary btn-sm" @click="saveNotes(search)">
                  <i v-if="savingNotes" class="fas fa-spinner fa-spin"></i>
                  <i v-else class="fas fa-check"></i>
                  Save
                </button>
                <button class="btn btn-ghost btn-sm" @click="cancelEditNotes">Cancel</button>
              </div>
            </div>

            <!-- Snapshot toggle -->
            <button
              v-if="hasSnapshot(search)"
              class="snapshot-toggle-btn"
              @click="toggleSnapshot(search.id)"
            >
              <i :class="expandedSnapshotId === search.id ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
              {{ expandedSnapshotId === search.id ? 'Hide' : 'View' }} Saved Results
              <span class="snapshot-meta">
                — snapshot from {{ formatDate(search.result_snapshot.snapshot_date || search.created_at) }}
              </span>
            </button>

            <!-- Snapshot panel -->
            <div v-if="expandedSnapshotId === search.id && hasSnapshot(search)" class="snapshot-panel">

              <!-- Evidence quality badge -->
              <div v-if="search.result_snapshot.evidence_quality" class="snapshot-section-header">
                <i class="fas fa-robot"></i>
                <strong>AI Clinical Summary</strong>
                <span class="evidence-badge" :class="`quality-${search.result_snapshot.evidence_quality}`">
                  {{ evidenceLabel(search.result_snapshot.evidence_quality) }}
                </span>
                <span v-if="search.result_snapshot.detected_species && search.result_snapshot.detected_species.length" class="species-chips">
                  <span v-for="sp in search.result_snapshot.detected_species" :key="sp" class="species-chip">{{ sp }}</span>
                </span>
              </div>
              <div
                v-if="search.result_snapshot.answer"
                class="snapshot-answer"
                v-html="formatAnswer(search.result_snapshot.answer)"
              />

              <!-- Courses -->
              <div v-if="search.result_snapshot.courses && search.result_snapshot.courses.length > 0">
                <div class="snapshot-section-header">
                  <i class="fas fa-graduation-cap"></i>
                  <strong>Courses Found ({{ search.result_snapshot.courses.length }})</strong>
                </div>
                <div class="snapshot-courses">
                  <div
                    v-for="course in search.result_snapshot.courses"
                    :key="course.id"
                    class="snapshot-course-item"
                  >
                    <router-link :to="`/learning/courses/${course.id}`" class="snapshot-course-title">
                      {{ course.title }}
                    </router-link>
                    <span v-if="courseInstructorLabel(course)" class="snapshot-course-meta">{{ courseInstructorLabel(course) }}</span>
                    <span v-if="course.credit_hours" class="snapshot-course-meta">{{ course.credit_hours }} CE hrs</span>
                  </div>
                </div>
              </div>

              <!-- Sources -->
              <div v-if="search.result_snapshot.sources && search.result_snapshot.sources.length > 0">
                <div class="snapshot-section-header">
                  <i class="fas fa-flask"></i>
                  <strong>PubMed Sources ({{ search.result_snapshot.sources.length }})</strong>
                </div>
                <div class="snapshot-sources">
                  <div
                    v-for="source in search.result_snapshot.sources"
                    :key="source.pmid"
                    class="snapshot-source-item"
                  >
                    <div class="snapshot-source-meta">
                      <span class="snapshot-source-journal">{{ source.journal }}</span>
                      <span v-if="source.year" class="snapshot-source-year">{{ source.year }}</span>
                    </div>
                    <a
                      :href="source.url || `https://pubmed.ncbi.nlm.nih.gov/${source.pmid}/`"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="snapshot-source-title"
                    >{{ source.title }}</a>
                    <p v-if="source.rationale" class="snapshot-source-rationale">
                      <i class="fas fa-lightbulb"></i> {{ source.rationale }}
                    </p>
                  </div>
                </div>
              </div>

            </div>

            <!-- Footer actions -->
            <div class="search-footer">
              <button class="btn btn-primary btn-sm" @click="rerunSearch(search)">
                <i class="fas fa-play"></i>
                Re-run
              </button>
              <button
                class="btn btn-ghost btn-sm delete-search-btn"
                title="Delete this saved search"
                @click="deleteSearch(search)"
              >
                <i class="fas fa-trash-alt"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { lmsAPI } from '@/services/lmsAPI.js'

export default {
  name: 'MyLibrary',

  setup() {
    const router = useRouter()

    const loading = ref(true)
    const activeTab = ref('articles')
    const articleFilter = ref('all')

    const articles = ref([])
    const savedSearches = ref([])
    const stats = ref({})
    const folderNames = ref([])

    // Snapshot expand/collapse
    const expandedSnapshotId = ref(null)

    const toggleSnapshot = (id) => {
      expandedSnapshotId.value = expandedSnapshotId.value === id ? null : id
    }

    const hasSnapshot = (search) => {
      const s = search.result_snapshot
      return s && (s.answer || (s.sources && s.sources.length) || (s.courses && s.courses.length))
    }

    const evidenceLabel = (q) => ({
      strong: 'Strong Evidence',
      moderate: 'Moderate Evidence',
      limited: 'Limited Evidence',
      no_evidence: 'Consensus Knowledge',
    }[q] || q)

    // Phase 1b — read instructors[] when present, fall back to legacy field.
    const courseInstructorLabel = (c) => {
      if (!c) return ''
      const list = Array.isArray(c.instructors) ? c.instructors : []
      const lead = [...list].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))[0]
      if (lead) return lead.user?.full_name || lead.full_name || lead.name || ''
      return c.instructor_name || ''
    }

    const formatAnswer = (text) => {
      if (!text) return ''
      return text
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/\n\n/g, '</p><p>')
        .replace(/\n/g, '<br>')
        .replace(/^/, '<p>')
        .replace(/$/, '</p>')
    }

    // Inline notes editing
    const editingNotesId = ref(null)
    const editNotesValue = ref('')
    const savingNotes = ref(false)

    const startEditNotes = (search) => {
      editingNotesId.value = search.id
      editNotesValue.value = search.notes || ''
    }

    const cancelEditNotes = () => {
      editingNotesId.value = null
      editNotesValue.value = ''
    }

    const saveNotes = async (search) => {
      if (savingNotes.value) return
      savingNotes.value = true
      try {
        await lmsAPI.updateSavedSearch(search.id, { notes: editNotesValue.value.trim() })
        search.notes = editNotesValue.value.trim()
        cancelEditNotes()
      } catch (e) {
        console.error('Failed to save notes:', e)
      } finally {
        savingNotes.value = false
      }
    }

    const filteredArticles = computed(() => {
      if (articleFilter.value === 'all') return articles.value
      if (articleFilter.value === 'favorites') return articles.value.filter(a => a.is_favorite)
      return articles.value.filter(a => a.folder === articleFilter.value)
    })

    const authorLine = (article) => {
      if (!article.authors) return ''
      return Array.isArray(article.authors)
        ? article.authors.join(', ')
        : article.authors
    }

    const truncate = (str, max) =>
      str && str.length > max ? str.slice(0, max) + '…' : str

    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
      })
    }

    const searchTypeLabel = (type) => {
      const map = {
        VETERINARY: 'AI Consult',
        ALL: 'All',
        COURSES: 'Courses',
        RESEARCH: 'Research',
      }
      return map[type] || type
    }

    const loadData = async () => {
      loading.value = true
      try {
        const [resourcesRes, searchesRes, statsRes, foldersRes] = await Promise.all([
          lmsAPI.getSavedResources(),
          lmsAPI.getSavedSearches(),
          lmsAPI.getResourceStats(),
          lmsAPI.getResourceFolders(),
        ])

        // Handle both paginated and plain list responses
        const rData = resourcesRes.data
        articles.value = rData.results ?? rData ?? []

        const sData = searchesRes.data
        savedSearches.value = sData.results ?? sData ?? []

        stats.value = statsRes.data?.stats ?? statsRes.data ?? {}

        const fData = foldersRes.data
        const rawFolders = fData?.folders ?? fData ?? []
        folderNames.value = rawFolders.map(f => (typeof f === 'string' ? f : f.folder)).filter(Boolean)
      } catch (e) {
        console.error('Failed to load library:', e)
      } finally {
        loading.value = false
      }
    }

    const toggleFavorite = async (article) => {
      try {
        const res = await lmsAPI.toggleResourceFavorite(article.id)
        article.is_favorite = res.data.is_favorite
        const delta = res.data.is_favorite ? 1 : -1
        stats.value.favorites = Math.max(0, (stats.value.favorites || 0) + delta)
      } catch (e) {
        console.error('Failed to toggle favorite:', e)
      }
    }

    const deleteArticle = async (article) => {
      const label = truncate(article.title, 60)
      if (!confirm(`Remove "${label}" from your library?`)) return
      try {
        await lmsAPI.deleteSavedResource(article.id)
        articles.value = articles.value.filter(a => a.id !== article.id)
        stats.value.total_saved = Math.max(0, (stats.value.total_saved || 0) - 1)
      } catch (e) {
        console.error('Failed to delete article:', e)
      }
    }

    const rerunSearch = async (search) => {
      try {
        await lmsAPI.executeSavedSearch(search.id)
        search.use_count = (search.use_count || 0) + 1
      } catch { /* non-critical */ }
      router.push({ path: '/learning', query: { autorun: search.query } })
    }

    const deleteSearch = async (search) => {
      if (!confirm(`Delete saved search "${search.name}"?`)) return
      try {
        await lmsAPI.deleteSavedSearch(search.id)
        savedSearches.value = savedSearches.value.filter(s => s.id !== search.id)
      } catch (e) {
        console.error('Failed to delete search:', e)
      }
    }

    onMounted(loadData)

    return {
      loading,
      activeTab,
      articleFilter,
      articles,
      savedSearches,
      stats,
      folderNames,
      filteredArticles,
      authorLine,
      truncate,
      formatDate,
      searchTypeLabel,
      toggleFavorite,
      deleteArticle,
      rerunSearch,
      deleteSearch,
      // snapshot
      expandedSnapshotId,
      toggleSnapshot,
      hasSnapshot,
      evidenceLabel,
      formatAnswer,
      courseInstructorLabel,
      // notes editing
      editingNotesId,
      editNotesValue,
      savingNotes,
      startEditNotes,
      cancelEditNotes,
      saveNotes,
    }
  },
}
</script>

<style scoped>
/* ── Layout ──────────────────────────────────────────── */
.my-library-page {
  padding-bottom: 2.5rem;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  flex-shrink: 0;
  flex-wrap: wrap;
}

/* ── Buttons ─────────────────────────────────────────── */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.125rem;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s;
  white-space: nowrap;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.btn-secondary {
  background: white;
  color: #374151;
  border: 1.5px solid #d1d5db;
}

.btn-secondary:hover {
  border-color: #667eea;
  color: #667eea;
}

.btn-ghost {
  background: transparent;
  color: #9ca3af;
  border: 1.5px solid #e5e7eb;
}

.btn-ghost:hover {
  color: #ef4444;
  border-color: #fca5a5;
  background: #fef2f2;
}

.btn-sm {
  padding: 0.375rem 0.875rem;
  font-size: 0.875rem;
}

/* ── Loading ─────────────────────────────────────────── */
.loading-container {
  text-align: center;
  padding: 4rem 1rem;
  color: #6b7280;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin { to { transform: rotate(360deg); } }

/* ── Stats Grid ──────────────────────────────────────── */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.75rem;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  flex-shrink: 0;
}

.stat-icon.articles  { background: #fef3c7; color: #d97706; }
.stat-icon.favorites { background: #fef9c3; color: #ca8a04; }
.stat-icon.searches  { background: #ede9fe; color: #7c3aed; }
.stat-icon.folders   { background: #e0f2fe; color: #0369a1; }

.stat-info h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.125rem;
  line-height: 1;
}

.stat-info p {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0;
}

/* ── Tab Bar ─────────────────────────────────────────── */
.library-tabs {
  display: flex;
  gap: 0.25rem;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 1.5rem;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  font-size: 0.9375rem;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-btn:hover {
  color: #374151;
}

.tab-btn.active {
  color: #667eea;
  border-bottom-color: #667eea;
  font-weight: 600;
}

.tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 700;
  border-radius: 20px;
}

.tab-btn.active .tab-count {
  background: #ede9fe;
  color: #7c3aed;
}

/* ── Filter Bar ──────────────────────────────────────── */
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.875rem;
  background: white;
  border: 1.5px solid #e5e7eb;
  border-radius: 20px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-chip:hover {
  border-color: #667eea;
  color: #667eea;
}

.filter-chip.active {
  background: #ede9fe;
  border-color: #a78bfa;
  color: #7c3aed;
  font-weight: 600;
}

.filter-chip.star-chip i { color: #f59e0b; }

/* ── Empty State ─────────────────────────────────────── */
.empty-state {
  text-align: center;
  padding: 4rem 1rem;
}

.empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #fef3c7;
  color: #d97706;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin: 0 auto 1.5rem;
}

.empty-state h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.625rem;
}

.empty-state p {
  color: #6b7280;
  font-size: 0.9375rem;
  margin: 0 0 1.5rem;
  max-width: 440px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
}

/* ── Articles List ───────────────────────────────────── */
.articles-list {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}

.article-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.25rem;
  transition: box-shadow 0.2s;
}

.article-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.article-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.625rem;
}

.article-meta {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.article-journal {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #92400e;
}

.article-year {
  font-size: 0.75rem;
  color: #9ca3af;
}

.article-actions {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-shrink: 0;
}

.action-btn {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
  border: 1.5px solid #e5e7eb;
  border-radius: 6px;
  color: #9ca3af;
  font-size: 0.8125rem;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s;
}

.action-btn:hover {
  border-color: #667eea;
  color: #667eea;
  background: #f5f3ff;
}

.action-btn.fav-active {
  color: #f59e0b;
  border-color: #fde68a;
  background: #fef9c3;
}

.action-btn.delete-btn:hover {
  border-color: #fca5a5;
  color: #ef4444;
  background: #fef2f2;
}

.article-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.375rem;
  line-height: 1.4;
}

.article-authors {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0 0 0.625rem;
}

.article-footer {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.article-pmid {
  font-size: 0.75rem;
  color: #9ca3af;
  font-family: monospace;
}

.article-via {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: #9ca3af;
  font-style: italic;
}

.article-via i {
  color: #a78bfa;
  font-size: 0.6875rem;
}

/* ── Searches List ───────────────────────────────────── */
.searches-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.search-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  transition: box-shadow 0.2s;
}

.search-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.search-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.search-type-badge {
  display: inline-block;
  padding: 0.2rem 0.625rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.type-veterinary { background: #ede9fe; color: #7c3aed; }
.type-all        { background: #f3f4f6; color: #4b5563; }
.type-courses    { background: #dbeafe; color: #1d4ed8; }
.type-research   { background: #fef3c7; color: #d97706; }

.search-meta-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.use-count {
  font-size: 0.75rem;
  color: #9ca3af;
}

.search-date {
  font-size: 0.75rem;
  color: #9ca3af;
}

.search-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  line-height: 1.3;
}

.search-query {
  font-size: 0.875rem;
  color: #6b7280;
  font-style: italic;
  margin: 0;
  line-height: 1.5;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.search-notes-row {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  min-height: 1.5rem;
}

.search-notes {
  flex: 1;
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: #6b7280;
  font-style: italic;
  margin: 0;
  line-height: 1.5;
}

.search-notes i {
  color: #f59e0b;
  font-size: 0.75rem;
  margin-top: 2px;
  flex-shrink: 0;
}

.edit-notes-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  transition: all 0.2s;
  margin-left: auto;
}

.edit-notes-btn:hover {
  color: #667eea;
  background: #f5f3ff;
}

.search-notes-edit {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.notes-textarea {
  width: 100%;
  padding: 0.5rem 0.625rem;
  border: 1.5px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #374151;
  resize: none;
  line-height: 1.5;
  font-family: inherit;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.notes-textarea:focus {
  outline: none;
  border-color: #667eea;
}

.notes-edit-actions {
  display: flex;
  gap: 0.5rem;
}

.search-footer {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
  flex-wrap: wrap;
}

.delete-search-btn {
  margin-left: auto;
}

/* ── Snapshot Toggle ─────────────────────────────────── */
.snapshot-toggle-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  text-align: left;
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  padding: 0.5rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 0.25rem;
}

.snapshot-toggle-btn:hover {
  background: #ede9fe;
  border-color: #a78bfa;
  color: #7c3aed;
}

.snapshot-meta {
  font-weight: 400;
  font-style: italic;
  color: #9ca3af;
  font-size: 0.75rem;
}

/* ── Snapshot Panel ──────────────────────────────────── */
.snapshot-panel {
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  padding: 1.25rem;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.25rem;
}

.snapshot-section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
}

.snapshot-section-header i {
  color: #667eea;
  font-size: 0.875rem;
}

.evidence-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.2rem 0.625rem;
  border-radius: 20px;
}

.quality-strong   { background: #d1fae5; color: #065f46; }
.quality-moderate { background: #fef3c7; color: #92400e; }
.quality-limited  { background: #fee2e2; color: #991b1b; }
.quality-no_evidence { background: #f3f4f6; color: #4b5563; }

.species-chips {
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
}

.species-chip {
  background: #ede9fe;
  color: #7c3aed;
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.125rem 0.5rem;
  border-radius: 20px;
  text-transform: capitalize;
}

.snapshot-answer {
  font-size: 0.875rem;
  line-height: 1.7;
  color: #374151;
  max-height: 260px;
  overflow-y: auto;
  padding-right: 0.25rem;
}

.snapshot-answer :deep(p) { margin: 0 0 0.625rem; }
.snapshot-answer :deep(p:last-child) { margin-bottom: 0; }
.snapshot-answer :deep(strong) { font-weight: 600; color: #1f2937; }

/* Courses */
.snapshot-courses {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.snapshot-course-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  flex-wrap: wrap;
}

.snapshot-course-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  text-decoration: none;
  flex: 1;
  min-width: 0;
}

.snapshot-course-title:hover {
  color: #667eea;
  text-decoration: underline;
}

.snapshot-course-meta {
  font-size: 0.75rem;
  color: #9ca3af;
  white-space: nowrap;
}

/* Sources */
.snapshot-sources {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.snapshot-source-item {
  padding: 0.625rem 0.875rem;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
}

.snapshot-source-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}

.snapshot-source-journal {
  font-size: 0.75rem;
  font-weight: 600;
  color: #92400e;
}

.snapshot-source-year {
  font-size: 0.7rem;
  color: #9ca3af;
}

.snapshot-source-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1f2937;
  text-decoration: none;
  display: block;
  line-height: 1.4;
  margin-bottom: 0.25rem;
}

.snapshot-source-title:hover {
  color: #667eea;
  text-decoration: underline;
}

.snapshot-source-rationale {
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  font-size: 0.8rem;
  color: #6b7280;
  font-style: italic;
  margin: 0;
  line-height: 1.5;
}

.snapshot-source-rationale i {
  color: #f59e0b;
  margin-top: 2px;
  flex-shrink: 0;
  font-size: 0.7rem;
}

/* ── Responsive ──────────────────────────────────────── */
@media (max-width: 900px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .list-header {
    flex-direction: column;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .header-actions {
    width: 100%;
  }

  .btn {
    flex: 1;
    justify-content: center;
  }

  .searches-list {
    grid-template-columns: 1fr;
  }
}
</style>
